import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ShieldCheck, 
  Truck, 
  FileCheck, 
  Activity,
  ArrowRight,
  Clock,
  ThermometerSun
} from "lucide-react";
import PricingRequestModal from "@/components/PricingRequestModal";
import PromoModal from "@/components/PromoModal";

const features = [
  {
    icon: Clock,
    title: "Fast, Reliable Results",
    description: "Point-of-care tests delivering accurate results in minutes, enabling timely clinical decisions."
  },
  {
    icon: ShieldCheck,
    title: "FDA Cleared & CLIA Waived",
    description: "Our tests meet rigorous regulatory standards for professional healthcare settings."
  },
  {
    icon: ThermometerSun,
    title: "Room Temperature Storage",
    description: "Convenient storage requirements reduce logistics complexity and costs."
  },
  {
    icon: Truck,
    title: "Reliable Supply Chain",
    description: "Dependable distribution ensuring your facility maintains adequate inventory."
  }
];

const products = [
  {
    id: "strep-a",
    name: "Strep A Antigen Test",
    sku: "MRK250006",
    description: "CLIA-waived rapid detection of Group A Streptococcus. Results in 5 minutes.",
    badge: "CLIA Waived",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/36hklcrv_healgen_strep_a.png"
  },
  {
    id: "covid-flu",
    name: "COVID-19 / Flu A & B Combo",
    sku: "MRK240016",
    description: "3-in-1 differential detection of SARS-CoV-2, Influenza A, and Influenza B.",
    badge: "EUA Authorized",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/bae9f7bt_healgen_covid_flu_pro.png"
  },
  {
    id: "rsv",
    name: "RSV Antigen Test",
    sku: "MRK240002",
    description: "Rapid RSV detection essential for pediatric and geriatric care settings.",
    badge: "FDA Cleared",
    image: "https://customer-assets.emergentagent.com/job_af70e0d8-2a27-4431-96d1-1c57842c8dea/artifacts/n2n838ro_healgen_rsv.png"
  }
];

export default function HomePage() {
  const [pricingModalOpen, setPricingModalOpen] = useState(false);

  return (
    <div>
      {/* Pricing Request Modal */}
      <PricingRequestModal open={pricingModalOpen} onOpenChange={setPricingModalOpen} />
      <PromoModal />

      {/* Hero Section */}
      <section className="relative bg-white hero-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="animate-fade-in">
              <span className="caption-text text-sky-600 mb-4 block">Professional Diagnostics Distribution</span>
              <h1 className="heading-primary mb-6" data-testid="hero-headline">
                Rapid, FDA-Authorized Diagnostic Tests for Clinical Use
              </h1>
              <p className="body-large mb-8 max-w-xl" data-testid="hero-subheadline">
                ClearPath Rapid Diagnostics distributes trusted point-of-care tests for clinics, urgent care, and healthcare providers across the U.S.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setPricingModalOpen(true)}
                  className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
                  data-testid="hero-request-pricing-btn"
                >
                  Request Pricing
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Link to="/contact" data-testid="hero-contact-sales-btn">
                  <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-base">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-fade-in-delay-1">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-200/50">
                <img 
                  src="https://healgen.com/wp-content/uploads/2025/07/healgen-rapid-check-covid-19-flu-ab-antigen-test-HP-1.png"
                  alt="HealGen Rapid Check diagnostic test products"
                  className="w-full aspect-[4/3] object-cover"
                  data-testid="hero-image"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">FDA Cleared</div>
                    <div className="text-xs text-slate-500">Regulatory Compliant</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-slate-50 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="caption-text text-sky-600 mb-4 block">Why Choose ClearPath</span>
            <h2 className="heading-secondary" data-testid="features-headline">
              Trusted by Healthcare Professionals
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title} 
                className="card-feature animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="feature-icon mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="body-default text-sm">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview Section */}
      <section className="bg-white section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <span className="caption-text text-sky-600 mb-2 block">Our Products</span>
              <h2 className="heading-secondary" data-testid="products-headline">
                Point-of-Care Diagnostic Tests
              </h2>
            </div>
            <Link to="/products" data-testid="view-all-products-btn">
              <Button variant="outline" className="border-slate-300">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <Link 
                key={product.id} 
                to={`/products/${product.id}`}
                className="card-product product-card group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="regulatory-badge">{product.badge}</span>
                    <span className="sku-badge">SKU: {product.sku}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {product.name}
                  </h3>
                  <p className="body-default text-sm mb-4">
                    {product.description}
                  </p>
                  <span className="text-sky-600 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-900 section-gap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="cta-headline">
            Ready to Optimize Your Diagnostic Capabilities?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
            Contact our team for pricing, product information, and to discuss your facility's diagnostic testing needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => setPricingModalOpen(true)}
              className="bg-sky-600 hover:bg-sky-700 text-white px-8 py-6 text-base"
              data-testid="cta-request-pricing-btn"
            >
              Request Pricing
            </Button>
            <Link to="/products" data-testid="cta-browse-products-btn">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-base">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="disclaimer-box text-center">
            <Activity className="w-5 h-5 inline-block mr-2" />
            <span className="font-medium">Professional Use Only:</span> All products are intended for professional in vitro diagnostic use by trained healthcare personnel. Not for consumer sale.
          </div>
        </div>
      </section>
    </div>
  );
}
